import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, message, Alert } from 'antd';
import { useCreate } from '@refinedev/core';
import { FormItemRenderer } from '@src/components/shared/forms/CommonForm/FormItemRenderer';
import { usePaymentsFormFields } from '../hooks/usePaymentsFormFields';

interface PaymentFormModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: (data: any) => void;
  customHeader?: {
    message: string;
    type?: 'info' | 'success' | 'warning' | 'error';
    showIcon?: boolean;
  };
  initialValues?: {
    type?: 'MEDICAL_PURCHASES' | 'MEDICAL_SALES' | 'PATIENT_INVOICE' | 'CLINIC_EXPENSES';
    reference_id?: number;
    transaction_type?: 'DEBIT' | 'CREDIT';
    amount?: number;
  };
}

export const PaymentFormModal: React.FC<PaymentFormModalProps> = ({
  visible,
  onClose,
  onSuccess,
  customHeader,
  initialValues,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const formFields = usePaymentsFormFields();
  const { mutate: createPayment } = useCreate();

  // Reset form and set initial values when modal opens
  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (initialValues) {
        setTimeout(() => {
          form.setFieldsValue(initialValues);
        }, 100);
      }
    }
  }, [visible, initialValues, form]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      createPayment(
        {
          resource: 'payments',
          values: values,
        },
        {
          onSuccess: data => {
            message.success('Payment created successfully');
            form.resetFields();
            onSuccess?.(data);
            onClose();
          },
          onError: error => {
            console.error('Payment creation error:', error);
            message.error('Failed to create payment');
          },
          onSettled: () => {
            setLoading(false);
          },
        }
      );
    } catch (error) {
      console.error('Payment creation error:', error);
      message.error('Failed to create payment');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Create Payment"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
        >
          Create Payment
        </Button>,
      ]}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        preserve={false}
      >
        {customHeader && (
          <Alert
            message={customHeader.message}
            type={customHeader.type || 'info'}
            showIcon={customHeader.showIcon !== false}
            style={{
              marginBottom: 16,
              fontSize: '16px',
              fontWeight: 500,
            }}
          />
        )}
        {formFields.map((field: any) => (
          <FormItemRenderer key={field.name} field={field} form={form} />
        ))}
      </Form>
    </Modal>
  );
};
